import dayjs from "dayjs"
import { useMemo } from "react"
import type { OperationContext } from "urql"
import { useQuery } from "urql"

import {
  GetPurchasingCostListDocument,
  type PurchasingCostListEntry,
  PurchasingCostListScope,
  type Query,
} from "@/graphql/codegen/graphql"

import "../graphql/queries"

interface UseHistoricPurchaseCostsParams {
  productId: number
  warehouseAreaId: string | null
}

interface UseHistoricPurchaseCostsReturn {
  entries: PurchasingCostListEntry[]
  isFetching: boolean
  refetch: (opts?: Partial<OperationContext>) => void
}

export const useHistoricPurchaseCosts = ({
  productId,
  warehouseAreaId,
}: UseHistoricPurchaseCostsParams): UseHistoricPurchaseCostsReturn => {
  const [{ data, fetching }, refetch] = useQuery<Pick<Query, "priceChange">>({
    query: GetPurchasingCostListDocument,
    variables: {
      input: {
        productId,
        warehouseAreaId: warehouseAreaId ? [warehouseAreaId] : undefined,
        scope: PurchasingCostListScope.History,
      },
    },
    pause: !warehouseAreaId,
  })

  const entries = useMemo<PurchasingCostListEntry[]>(() => {
    const output = data?.priceChange?.getPurchasingCostList
    if (output?.__typename !== "GetPurchasingCostListSuccess") return []
    return output.entries.slice().sort((a, b) => dayjs(b.effectiveDate).diff(dayjs(a.effectiveDate)))
  }, [data])

  return { entries, isFetching: fetching, refetch }
}
